
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { useStoreSettings } from '../context/StoreSettingsContext';
import { getRandomBirthdayMessage } from '../lib/birthdayMessages';
import { supabase } from '../lib/supabase';
import { Gift, X, Calendar, Sparkles, Loader2, Copy, Check } from 'lucide-react';
import WhatsAppIcon from './ui/WhatsAppIcon';

export const BirthdayNotification: React.FC = () => {
  const { customers } = useData();
  const { storeSettings } = useStoreSettings();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [generatingFor, setGeneratingFor] = useState<string | null>(null);
  const [aiMessages, setAiMessages] = useState<Record<string, string>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.getMonth();

  const birthdays = customers.filter(c => {
    if (!c.birthDate) return false;
    const parts = c.birthDate.split('-');
    if (parts.length !== 3) return false;
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return month === currentMonth && day >= currentDay;
  }).sort((a, b) => {
    const dayA = parseInt(a.birthDate!.split('-')[2], 10);
    const dayB = parseInt(b.birthDate!.split('-')[2], 10);
    return dayA - dayB;
  });

  const generateAiMessage = async (customerId: string, customerName: string, age: number | string) => {
    setGeneratingFor(customerId);
    try {
      const { data: settings } = await supabase.functions.invoke('manage-settings', {
        body: { action: 'GET_DECRYPTED', key: 'pollinations_api_key' },
      });

      const apiKey = settings?.value;
      if (!apiKey) {
        setAiMessages(prev => ({
          ...prev,
          [customerId]: `Olá ${customerName}, parabéns pelo seu aniversário! 🎂 Desejamos muita saúde e felicidade! ✨`,
        }));
        return;
      }

      const response = await fetch('https://gen.pollinations.ai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'openai-fast',
          messages: [
            {
              role: 'system',
              content: 'Você é um assistente de uma ótica (loja de óculos). Gere uma mensagem de parabéns curta e carinhosa para enviar via WhatsApp. Use emojis moderadamente. A mensagem deve ser profissional mas calorosa, mencionando o nome do cliente. Máximo 3 frases. Responda SOMENTE com a mensagem, sem aspas nem explicações.',
            },
            {
              role: 'user',
              content: `Gere uma mensagem de feliz aniversário para o cliente ${customerName} que está completando ${age} anos hoje.`,
            },
          ],
          max_tokens: 200,
          temperature: 0.8,
        }),
      });

      if (!response.ok) throw new Error('Erro na API');

      const data = await response.json();
      const message = data.choices?.[0]?.message?.content?.trim() || '';

      setAiMessages(prev => ({ ...prev, [customerId]: message }));
    } catch (err) {
      console.error('Erro ao gerar mensagem:', err);
      setAiMessages(prev => ({
        ...prev,
        [customerId]: `Olá ${customerName}, parabéns pelo seu aniversário! 🎂 Desejamos muita saúde e felicidade! ✨`,
      }));
    } finally {
      setGeneratingFor(null);
    }
  };

  const copyAndOpenWhatsApp = (customerId: string, phone: string, name: string) => {
    let message = aiMessages[customerId];
    if (!message) {
      const result = getRandomBirthdayMessage(name, storeSettings.attendantName, storeSettings.storeName);
      message = result.message;
    }
    const cleanPhone = phone.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/55${cleanPhone}?text=${encodeURIComponent(message)}`;

    navigator.clipboard.writeText(message).then(() => {
      setCopiedId(customerId);
      setTimeout(() => setCopiedId(null), 2000);
    });

    window.open(whatsappUrl, '_blank');
  };

  if (birthdays.length === 0) return null;

  return (
    <div className="relative z-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full text-red-500 transition-all duration-300 group animate-glow-red hover:scale-105"
        title={`${birthdays.length} aniversariante(s) este mês!`}
      >
        <Gift className="w-6 h-6" />
        <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-red-500/15 border border-red-500/30 text-[10px] font-bold text-red-700 dark:text-red-400 shadow-sm ring-2 ring-white dark:ring-slate-800">
          {birthdays.length}
        </span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-2 text-slate-900 dark:text-slate-50">
                <Gift className="w-5 h-5" />
                <span className="font-bold">Aniversariantes do Mês</span>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-50">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[28rem] overflow-y-auto py-2">
              {birthdays.map(customer => {
                const age = customer.birthDate
                  ? today.getFullYear() - parseInt(customer.birthDate.split('-')[0])
                  : '?';
                const hasAiMessage = !!aiMessages[customer.id];
                const isGenerating = generatingFor === customer.id;

                return (
                  <div key={customer.id} className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 last:border-0 hover:bg-slate-100 dark:hover:bg-[#26334D] transition-colors">
                    <div className="flex items-center justify-between">
                      <div
                        onClick={() => {
                          setIsOpen(false);
                          navigate('/customers', { state: { viewCustomerId: customer.id } });
                        }}
                        className="cursor-pointer flex-1"
                      >
                        <p className="font-semibold text-slate-900 dark:text-blue-400">{customer.name}</p>
                        <div className="flex items-center text-xs text-slate-600 dark:text-slate-400 gap-2 mt-0.5">
                          <span className="flex items-center gap-1 bg-red-500/15 border border-red-500/30 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded">
                            <Calendar className="w-3 h-3" />
                            {age} anos
                          </span>
                          <span className="text-[10px] text-slate-600 dark:text-slate-400">{customer.birthDate ? new Date(customer.birthDate + 'T00:00:00').toLocaleDateString('pt-BR') : ''}</span>
                          <span className="text-[10px] text-blue-700 dark:text-blue-400 hover:underline">Ver perfil</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => generateAiMessage(customer.id, customer.name, age)}
                          disabled={isGenerating}
                          className="p-2 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-500/30 transition-colors disabled:opacity-50"
                          title="Gerar mensagem com IA"
                        >
                          {isGenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4" />
                          )}
                        </button>

                        {customer.phone && (
                          <button
                            onClick={() => copyAndOpenWhatsApp(customer.id, customer.phone, customer.name)}
                            className="p-2 bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-300 rounded-full hover:bg-green-200 dark:hover:bg-green-500/30 transition-colors"
                            title={hasAiMessage ? 'Enviar mensagem IA no WhatsApp' : 'Enviar parabéns no WhatsApp'}
                          >
                            <WhatsAppIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* AI generated message preview */}
                    {hasAiMessage && (
                      <div className="mt-2 p-2.5 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-300 dark:border-slate-700">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed flex-1">{aiMessages[customer.id]}</p>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(aiMessages[customer.id]);
                              setCopiedId(customer.id);
                              setTimeout(() => setCopiedId(null), 2000);
                            }}
                            className="text-blue-700 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 flex-shrink-0"
                            title="Copiar mensagem"
                          >
                            {copiedId === customer.id ? (
                              <Check className="w-3.5 h-3.5 text-green-500" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="bg-slate-50 dark:bg-slate-900 px-4 py-2 text-center text-xs text-slate-600 dark:text-slate-400">
              Total: {birthdays.length} cliente(s) este mês · <Sparkles className="w-3 h-3 inline" /> IA gera mensagens personalizadas
            </div>
          </div>
        </>
      )}
    </div>
  );
};
